import { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { usePlayer } from '@/contexts/PlayerContext';

const POST_MOODS = [
    { v: 'calm', emoji: '🌿' },
    { v: 'grateful', emoji: '🍃' },
    { v: 'energized', emoji: '☀️' },
    { v: 'tired', emoji: '🌙' },
    { v: 'anxious', emoji: '🌧' },
    { v: 'stressed', emoji: '🌫' },
    { v: 'sad', emoji: '🫧' },
    { v: 'neutral', emoji: '◯' },
];

/**
 * Small bottom sheet that appears after a session is logged (only for
 * meditation kinds). Fully optional, dismissible — never blocking.
 */
export default function PostMoodToast() {
    const { postMoodSession, logPostMood, dismissPostMood } = usePlayer();
    const [savedMood, setSavedMood] = useState(null);

    // Reset acknowledgement when a new post-mood session appears.
    useEffect(() => {
        if (postMoodSession) setSavedMood(null);
    }, [postMoodSession]);

    if (!postMoodSession) return null;

    const pick = async (mood) => {
        setSavedMood(mood);
        await logPostMood(mood);
        setTimeout(() => dismissPostMood(), 1400);
    };

    return (
        <div
            className="fixed bottom-24 md:bottom-6 left-1/2 -translate-x-1/2 z-40 max-w-md w-[calc(100%-1.5rem)] qm-glass border-white/60 shadow-xl rounded-3xl p-4 animate-fade-in"
            role="status"
            data-testid="post-mood-toast"
        >
            <div className="flex items-start gap-3">
                <div className="flex-1 min-w-0">
                    {savedMood ? (
                        <div className="text-sm text-forest" data-testid="post-mood-saved">
                            Noted — thank you. 🌿
                        </div>
                    ) : (
                        <>
                            <div className="text-xs uppercase tracking-[0.25em] text-forest-soft">After the session</div>
                            <div className="font-serif text-lg text-forest mt-0.5 leading-tight">How do you feel now?</div>
                            <div className="flex flex-wrap gap-1.5 mt-2.5">
                                {POST_MOODS.map((m) => (
                                    <button
                                        key={m.v}
                                        type="button"
                                        onClick={() => pick(m.v)}
                                        data-testid={`post-mood-${m.v}`}
                                        className="qm-pill text-xs border-forest/20 hover:bg-sage/30 bg-white/60"
                                        aria-label={m.v}
                                    >
                                        <span className="mr-1">{m.emoji}</span>{m.v}
                                    </button>
                                ))}
                            </div>
                        </>
                    )}
                </div>
                <button
                    type="button"
                    onClick={dismissPostMood}
                    aria-label="Dismiss"
                    data-testid="post-mood-dismiss"
                    className="rounded-full w-8 h-8 grid place-items-center text-forest-soft hover:bg-dusty/40 hover:text-forest shrink-0"
                >
                    <X size={14} />
                </button>
            </div>
        </div>
    );
}
